import { queryOptions } from '@tanstack/react-query'

import type {
  ChangeEmailDto,
  ChangePasswordDto,
  LoginAndRefreshRes,
  LoginDto,
  LogoutDto,
  PatchAuthUserDto,
  RefreshDto,
  SuccessRes,
} from '@brickup/shared/dto'
import { ACCOUNT } from '@brickup/shared/locales/de/account'
import type { AuthUser } from '@brickup/shared/types'
import { apiClient, publicClient } from '@/api/client'
import { clearStorage, getDeviceId, setTokens } from '@/api/client-storage'
import { SnackbarType } from '@/components/Snackbar'
import { useAppStore } from '@/stores'
import { useSnackbarStore } from '@/stores/use-snackbar-store'

const setAuthenticated = (
  roles: Parameters<
    ReturnType<typeof useAppStore.getState>['setAuthenticated']
  >[0],
) => useAppStore.getState().setAuthenticated(roles)
const clearAuthenticationState = () =>
  useAppStore.getState().clearAuthenticationState()

export async function login(login: LoginDto): Promise<void> {
  const response = await publicClient.post<LoginAndRefreshRes>(
    '/auth/login',
    login,
  )
  setTokens(response.data)
}

export async function refresh(refresh: RefreshDto): Promise<string> {
  const { data } = await publicClient.post<LoginAndRefreshRes>(
    '/auth/refresh',
    refresh,
  )
  setTokens(data)

  return data.accessToken
}

export async function getAuthUser() {
  const { data } = await apiClient.get<AuthUser>(`/auth/user`)

  setAuthenticated(data.roles)

  return data
}

export const AUTH_USER_QUERY_KEY = ['user'] as const

/** The signed-in user. One definition, so the route guard and every screen
 *  that reads the user share a cache entry rather than racing two. */
export const authUserQuery = queryOptions({
  queryKey: AUTH_USER_QUERY_KEY,
  queryFn: getAuthUser,
  refetchOnWindowFocus: false,
  retry: false,
})

export async function patchAuthUser(dto: PatchAuthUserDto) {
  const { data } = await apiClient.patch<AuthUser>(`/auth/user`, dto)

  return data
}

export async function logout(logout?: LogoutDto): Promise<void> {
  const deviceId = getDeviceId()!

  if (!logout) logout = { deviceId }

  try {
    await apiClient.post<SuccessRes>(`/auth/logout`, logout)
  } finally {
    // Whatever the server said. A failed request that leaves the tokens in
    // place would hand the session to whoever opens the app next, while the
    // screen has already returned to the login page.
    if (deviceId === logout.deviceId) {
      clearAuthenticationState()
      clearStorage()
    }
  }
}

export async function logoutAllDevices(): Promise<void> {
  try {
    // No body: the endpoint ends every session of whoever holds the token.
    await apiClient.post<SuccessRes>(`/auth/logout-all-devices`)
  } finally {
    clearAuthenticationState()
    clearStorage()
  }

  // Show snackbar notification
  const { addSnackbar } = useSnackbarStore.getState()
  addSnackbar({
    title: ACCOUNT.loggedOutAllDevices,
    type: SnackbarType.INFO,
  })
}

export async function changeEmail(changeEmail: ChangeEmailDto): Promise<void> {
  await apiClient.post<SuccessRes>('/auth/change-email', changeEmail)
}

export async function changePassword(
  changePassword: ChangePasswordDto,
): Promise<void> {
  await apiClient.post<SuccessRes>('/auth/change-password', changePassword)
  await logoutAllDevices()
}

export async function forgotPassword(email: string): Promise<SuccessRes> {
  const { data } = await publicClient.post<SuccessRes>(
    '/auth/forgot-password',
    {
      email,
    },
  )
  return data
}

export async function validateResetToken(
  token: string,
): Promise<{ valid: boolean; email?: string }> {
  const { data } = await publicClient.get<{ valid: boolean; email?: string }>(
    `/auth/reset-password/${token}`,
  )
  return data
}

export async function resetPassword(
  token: string,
  newPassword: string,
): Promise<SuccessRes> {
  const { data } = await publicClient.post<SuccessRes>('/auth/reset-password', {
    token,
    newPassword,
  })
  return data
}
